import { SecondaryButton } from "@/components/buttons/SecondaryButton";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import ActionTextIcon from "@/components/icons/ActionTextIcon";
import AntIcon from "@/components/icons/AntIcon";
import { ItemMetaInfo, ItemMetaInfoEntry } from "@/components/ItemMetaInfo";
import StatusTag from "@/components/tags/StatusTag";
import { PageHeader } from "@/components/views/PageHeader";
import { formatLocaleDate, formatLocaleDateTime } from "@/utils/formatUtils";
import { useBackNavigation } from "@/utils/hooksUtils";
import { tBoolean } from "@/utils/translationUtils";
import { MenuInfo } from "@rc-component/menu/es/interface";
import { Alert, App as AntApp, Button, Card, Divider, Dropdown, Space, Steps, Tabs, Tag, Tooltip, Upload } from "antd";
import { ItemType } from "antd/es/menu/interface";
import { t } from "i18next";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import {
  useChangeCommissionsBatchStep,
  useDeleteCommissionsBatch,
  useDownloadCommissionsBatchOutputsExport,
  useImportSpecialCommissions,
} from "../../../../common/api/queries";
import { FileUploadAccept, PresetColors } from "../../../../common/constants";
import { Permission } from "../../../../common/security/enums";
import { useHasPermissions, useIsCurrentAgentTypePlus, useIsSystemAdmin } from "../../../auth/hooks";
import { CommissionsBatchStep, CommissionsBatchStepDirection } from "../enums";
import { COMMISSIONS_BATCH_ROUTE_PATHS } from "../paths";
import { CommissionsBatch } from "../types";
import { isCommissionAdvanceBatch } from "../utils";
import { CommissionsBatchAmounts } from "./CommissionsBatchAmounts";
import { CommissionsBatchOutputs } from "./CommissionsBatchOutputs";
import { CommissionsBatchStepTag } from "./CommissionsBatchStepTag";
import { CommissionsBatchChangeStepModalForm } from "./forms/CommissionsBatchChangeStepModalForm";
import { CommissionsBatchPaymentOrderForm } from "./forms/CommissionsBatchPaymentOrderForm";
import { UpdateCommissionsBatchForm } from "./forms/UpdateCommissionsBatchForm";
import { CommissionBatchFinishedDateFormModal } from "./modals/CommissionBatchFinishedDateFormModal";
import { ExportInactiveAgentsFileTypeQuestionModal } from "./modals/ExportInactiveAgentsFileTypeQuestionModal";
import { ExportInputsFileTypeQuestionModal } from "./modals/ExportInputsFileTypeQuestionModal";
import { CommissionsBatchCalculationStep } from "./steps/CommissionsBatchCalculationStep";
import { CommissionsBatchFinishStep } from "./steps/CommissionsBatchFinishStep";
import { CommissionsBatchImportStep } from "./steps/CommissionsBatchImportStep";

const getTags = (batch: CommissionsBatch, operationInProgress: boolean, isTopAgentPlus?: boolean) => {
  const tags = [
    <CommissionsBatchStepTag key={1} step={batch.step} />,
    isTopAgentPlus ? (
      <Tag key={2} color={PresetColors.MAGENTA}>
        {t(`commissions.batch.enums.commissionBatchType_PLUS.${batch.type}`)}
      </Tag>
    ) : undefined,
  ];

  if (operationInProgress) {
    tags.push(<StatusTag key={3} status="processing" />);
  }

  return tags;
};

interface Props {
  batch: CommissionsBatch;
}

export const CommissionsBatchDetailView = ({ batch }: Props) => {
  const { t } = useTranslation();
  const { modal, notification } = AntApp.useApp();

  const navigate = useNavigate();

  const handleBack = useBackNavigation();

  const [batchFormOpen, setBatchFormOpen] = useState(false);
  const [isChangeStepModalOpen, setIsChangeStepModalOpen] = useState(false);
  const [paymentOrderFormOpen, setPaymentOrderFormOpen] = useState(false);
  const [isExportInputsModalOpen, setIsExportInputsModalOpen] = useState(false);
  const [isExportInactiveAgentsModalOpen, setIsExportInactiveAgentsModalOpen] = useState(false);
  const [isUpdateCommissionsBatchFinishedDateModalOpen, setIsUpdateCommissionsBatchFinishedDateModalOpen] =
    useState(false);

  const isSystemAdmin = useIsSystemAdmin();
  const isTopAgentPlus = useIsCurrentAgentTypePlus();
  const isAdvanceBatch = isCommissionAdvanceBatch(batch.type, isTopAgentPlus);
  const hasCommissionsManagePermission = useHasPermissions(Permission.COMMISSIONS_MANAGE);

  const downloadCommissionsBatchOutputsExport = useDownloadCommissionsBatchOutputsExport();
  const importSpecialCommissions = useImportSpecialCommissions();
  const deleteCommissionsBatch = useDeleteCommissionsBatch();
  const changeCommissionsBatchStep = useChangeCommissionsBatchStep();

  const inputImportInProgress = batch.inputs.some((a) => a.importInProgress);
  const operationInProgress = batch.stepChangeInProgress || inputImportInProgress;

  const handleStepChange = (direction: CommissionsBatchStepDirection) => {
    if (batch.step === CommissionsBatchStep.CALCULATION && direction === CommissionsBatchStepDirection.PREVIOUS) {
      modal.confirm({
        title: t("commissions.batch.helpers.returnToImportStepConfirm"),
        okText: t("common.yes"),
        cancelText: t("common.cancel"),
        onOk: () => {
          changeCommissionsBatchStep.mutate({
            id: batch.id,
            object: { optimisticLockVersion: batch.optimisticLockVersion, direction },
          });
        },
      });
    } else if (
      isTopAgentPlus &&
      batch.step === CommissionsBatchStep.IMPORT &&
      direction === CommissionsBatchStepDirection.NEXT
    ) {
      setIsChangeStepModalOpen(true);
    } else {
      changeCommissionsBatchStep.mutate({
        id: batch.id,
        object: { optimisticLockVersion: batch.optimisticLockVersion, direction },
      });
    }
  };

  const handleDropdownClick = (clickInfo: MenuInfo) => {
    switch (clickInfo.key) {
      case "edit":
        setBatchFormOpen(true);
        break;
      case "delete":
        modal.confirm({
          title: t("commissions.batch.helpers.deleteBatchConfirm"),
          okText: t("common.yes"),
          cancelText: t("common.no"),
          onOk: () =>
            deleteCommissionsBatch.mutate(batch.id, {
              onSuccess: () => navigate(COMMISSIONS_BATCH_ROUTE_PATHS.list.to, { replace: true }),
            }),
        });
        break;
      case "exportInputs":
        setIsExportInputsModalOpen(true);
        break;
      case "exportOutputs":
        downloadCommissionsBatchOutputsExport.mutate(batch.id);
        break;
      case "exportInactiveAgents":
        setIsExportInactiveAgentsModalOpen(true);
        break;
    }
  };

  const getDropdownMenuItems = (batch: CommissionsBatch, hasCommissionsManagePermission: boolean): ItemType[] => {
    const items: (ItemType & { visibility: boolean })[] = [
      {
        key: "edit",
        label: t("common.edit"),
        icon: <AntIcon type="edit" />,
        disabled: batch.stepChangeInProgress,
        visibility: hasCommissionsManagePermission && batch.step !== CommissionsBatchStep.FINISH,
      },
      {
        key: "delete",
        label: t("common.delete"),
        icon: <AntIcon type="delete" />,
        danger: true,
        disabled: batch.stepChangeInProgress,
        visibility: hasCommissionsManagePermission && batch.step !== CommissionsBatchStep.FINISH,
      },
      {
        key: "manageDivider",
        type: "divider",
        visibility: hasCommissionsManagePermission && batch.step !== CommissionsBatchStep.FINISH,
      },
      {
        key: "exportInputs",
        label: t("commissions.batch.actions.exportInputs"),
        icon: <AntIcon type="download" />,
        disabled: batch.stepChangeInProgress,
        visibility: true,
      },
      {
        key: "exportOutputs",
        label: t("commissions.batch.actions.exportOutputs"),
        icon: <AntIcon type="download" />,
        disabled: batch.stepChangeInProgress,
        visibility: [
          CommissionsBatchStep.RECAPITULATION,
          CommissionsBatchStep.REVIEW,
          CommissionsBatchStep.FINISH,
        ].includes(batch.step),
      },
      {
        key: "exportInactiveAgents",
        label: t("commissions.batch.actions.exportInactiveAgents"),
        icon: <AntIcon type="download" />,
        disabled: batch.stepChangeInProgress,
        visibility:
          isTopAgentPlus &&
          [CommissionsBatchStep.RECAPITULATION, CommissionsBatchStep.REVIEW, CommissionsBatchStep.FINISH].includes(
            batch.step,
          ),
      },
      {
        key: "importSpecialCommissions",
        label: (
          <Upload
            accept={FileUploadAccept.CSV}
            beforeUpload={(data) => {
              const formData = new FormData();
              formData.append("file", data);

              importSpecialCommissions.mutate(
                { id: batch.id, object: formData },
                {
                  onSuccess: () =>
                    notification.success({
                      title: t("common.operationSuccess"),
                      description: t("commissions.batch.helpers.importFinished"),
                    }),
                },
              );

              return false;
            }}
            showUploadList={false}
            disabled={batch.stepChangeInProgress}
          >
            {t("commissions.batch.actions.importSpecialCommissions")}
          </Upload>
        ),
        icon: <AntIcon type="import" />,
        disabled: batch.stepChangeInProgress,
        visibility: CommissionsBatchStep.RECAPITULATION === batch.step && hasCommissionsManagePermission,
      },
    ];

    return items
      .filter((item) => item.visibility)
      .map((item) => {
        const { visibility, ...data } = item;
        return data;
      });
  };

  const batchStepsStatus = batch.stepChangeError
    ? "error"
    : batch.step === CommissionsBatchStep.FINISH
      ? "finish"
      : "process";

  const batchSteps = (
    <Steps
      style={{ paddingBlock: "12px 48px" }}
      current={Object.keys(CommissionsBatchStep).indexOf(batch.step)}
      status={batchStepsStatus}
      items={[
        {
          icon: inputImportInProgress && <AntIcon type="loading" />,
          title: t("commissions.batch.enums.batchStep." + CommissionsBatchStep.IMPORT),
        },
        {
          icon: batch.upcomingStep === CommissionsBatchStep.CALCULATION && <AntIcon type="loading" />,
          title: t("commissions.batch.enums.batchStep." + CommissionsBatchStep.CALCULATION),
        },
        {
          icon: batch.upcomingStep === CommissionsBatchStep.RECAPITULATION && <AntIcon type="loading" />,
          title: t("commissions.batch.enums.batchStep." + CommissionsBatchStep.RECAPITULATION),
        },
        {
          icon: batch.upcomingStep === CommissionsBatchStep.REVIEW && <AntIcon type="loading" />,
          title: t("commissions.batch.enums.batchStep." + CommissionsBatchStep.REVIEW),
        },
        {
          icon: batch.upcomingStep === CommissionsBatchStep.FINISH && <AntIcon type="loading" />,
          title: t("commissions.batch.enums.batchStep." + CommissionsBatchStep.FINISH),
        },
      ]}
    />
  );

  const dropdownMenu = (
    <Dropdown
      trigger={["click"]}
      placement="bottomRight"
      menu={{
        items: getDropdownMenuItems(batch, hasCommissionsManagePermission),
        onClick: handleDropdownClick,
      }}
    >
      <Button icon={<AntIcon type="more" />} />
    </Dropdown>
  );

  const isOutputStep = [
    CommissionsBatchStep.RECAPITULATION,
    CommissionsBatchStep.REVIEW,
    CommissionsBatchStep.FINISH,
  ].includes(batch.step);

  const metaEntries: ItemMetaInfoEntry[] = [
    batch.calculationDate && {
      key: "calculationDate",
      label: t("commissions.batch.attrs.calculationDate"),
      value: formatLocaleDate(batch.calculationDate),
    },
    (batch.finishedAt || isOutputStep) && {
      key: "finishedAt",
      label: t("commissions.batch.attrs.finishedAt"),
      value: (
        <>
          {batch.finishedAt ? formatLocaleDateTime(batch.finishedAt) : undefined}
          {isOutputStep && hasCommissionsManagePermission && isSystemAdmin ? (
            <Tooltip title={t("common.edit")}>
              <span className="margin-left-tiny">
                <ActionTextIcon
                  icon="edit"
                  color="blue"
                  disabled={batch.stepChangeInProgress}
                  onClick={() => setIsUpdateCommissionsBatchFinishedDateModalOpen(true)}
                />
              </span>
            </Tooltip>
          ) : undefined}
        </>
      ),
    },
    isAdvanceBatch && {
      key: "closesCommissionAdvanceMonth",
      label: t("commissions.batch.attrs.closesCommissionAdvanceMonth"),
      value: (
        <LabelWithTooltip
          label={tBoolean(batch.closesCommissionAdvanceMonth)}
          tooltip={t("commissions.batch.helpers.closesCommissionAdvanceMonthDesc")}
        />
      ),
    },
  ].filter((entry) => !!entry);

  return (
    <>
      <PageHeader
        onBack={handleBack}
        title={t("commissions.batch.titles.batchDetail", { name: batch.name })}
        tags={getTags(batch, operationInProgress, isTopAgentPlus)}
        extra={
          hasCommissionsManagePermission ? (
            batch.step === CommissionsBatchStep.FINISH ? (
              <Space>
                <SecondaryButton icon={<AntIcon type="bank" />} onClick={() => setPaymentOrderFormOpen(true)}>
                  {t("commissions.batch.actions.generatePaymentOrder")}
                </SecondaryButton>

                {dropdownMenu}
              </Space>
            ) : (
              <Space>
                <Button
                  icon={<AntIcon type="double-left" />}
                  disabled={batch.step === CommissionsBatchStep.IMPORT || operationInProgress}
                  onClick={() => handleStepChange(CommissionsBatchStepDirection.PREVIOUS)}
                >
                  {t("commissions.batch.actions.batchPreviousStep")}
                </Button>

                <Button
                  type="primary"
                  icon={<AntIcon type="double-right" />}
                  disabled={operationInProgress}
                  onClick={() => handleStepChange(CommissionsBatchStepDirection.NEXT)}
                >
                  {t("commissions.batch.actions.batchNextStep")}
                </Button>

                {(batch.step === CommissionsBatchStep.RECAPITULATION || batch.step === CommissionsBatchStep.REVIEW) && (
                  <SecondaryButton icon={<AntIcon type="bank" />} onClick={() => setPaymentOrderFormOpen(true)}>
                    {t("commissions.batch.actions.generatePaymentOrder")}
                  </SecondaryButton>
                )}

                {dropdownMenu}
              </Space>
            )
          ) : (
            dropdownMenu
          )
        }
      >
        <ItemMetaInfo item={batch} entries={metaEntries} style={{ marginTop: -12, paddingLeft: 30 }} />

        <Divider size="medium" />

        {batchSteps}

        {batch.stepChangeError && (
          <Alert
            className="margin-bottom-medium"
            title={t("commissions.batch.helpers.batchStepChangeError")}
            description={batch.stepChangeError.description}
            type="error"
            showIcon
          />
        )}

        <CommissionsBatchAmounts batch={batch} />
      </PageHeader>

      <Tabs
        activeKey={batch.step}
        className="margin-top-medium"
        renderTabBar={() => <></>}
        items={[
          {
            key: CommissionsBatchStep.IMPORT,
            label: null,
            children: <CommissionsBatchImportStep batch={batch} />,
          },
          {
            key: CommissionsBatchStep.CALCULATION,
            label: null,
            children: <CommissionsBatchCalculationStep batch={batch} />,
          },
          {
            key: CommissionsBatchStep.RECAPITULATION,
            label: null,
            children: (
              <Card className="card-box">
                <CommissionsBatchOutputs batch={batch} />
              </Card>
            ),
          },
          {
            key: CommissionsBatchStep.REVIEW,
            label: null,
            children: (
              <Card className="card-box">
                <CommissionsBatchOutputs batch={batch} />
              </Card>
            ),
          },
          {
            key: CommissionsBatchStep.FINISH,
            label: null,
            children: <CommissionsBatchFinishStep batch={batch} />,
          },
        ]}
        destroyOnHidden
      />

      <CommissionBatchFinishedDateFormModal
        batch={batch}
        isOpen={isUpdateCommissionsBatchFinishedDateModalOpen}
        onClose={() => setIsUpdateCommissionsBatchFinishedDateModalOpen(false)}
      />

      <ExportInputsFileTypeQuestionModal
        isOpen={isExportInputsModalOpen}
        onClose={() => setIsExportInputsModalOpen(false)}
        batchId={batch.id}
      />

      <ExportInactiveAgentsFileTypeQuestionModal
        isOpen={isExportInactiveAgentsModalOpen}
        onClose={() => setIsExportInactiveAgentsModalOpen(false)}
        batchId={batch.id}
      />

      <CommissionsBatchChangeStepModalForm
        isOpen={isChangeStepModalOpen}
        onCancel={() => setIsChangeStepModalOpen(false)}
        batch={batch}
      />

      <UpdateCommissionsBatchForm batch={batch} open={batchFormOpen} onFormCancel={() => setBatchFormOpen(false)} />

      <CommissionsBatchPaymentOrderForm
        batch={batch}
        open={paymentOrderFormOpen}
        onFormCancel={() => setPaymentOrderFormOpen(false)}
      />
    </>
  );
};
